class MainSearch extends SearchForm {
  constructor() {
    super();
    this.isHeaderSearch = Boolean(this.closest('.header__search'));
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.searchDropdown = document.querySelector('[data-header-search-dropdown]');
    this.searchOverlay = document.querySelector('[data-header-search-overlay]');
    this.pointerDownInsideFlyout = false;
    this.onPointerDownHandler = this.onPointerDown.bind(this);
    if (this.isHeaderSearch) {
      document.addEventListener('pointerdown', this.onPointerDownHandler);
    }
    this.onDocumentClickHandler = this.onDocumentClick.bind(this);
    this.onEscapeKeyHandler = this.onEscapeKey.bind(this);
    this.flyoutJustOpened = false;
    this.blurTimeout = null;
    this.setupEventListeners();
    this.viewportQuery = window.matchMedia('(max-width: 989px)');
    this.viewportChangeHandler = this.onViewportChange.bind(this);
    if (this.viewportQuery.addEventListener) {
      this.viewportQuery.addEventListener('change', this.viewportChangeHandler);
    } else if (this.viewportQuery.addListener) {
      this.viewportQuery.addListener(this.viewportChangeHandler);
    }
  }

  getHeaderElement() {
    return document.querySelector('.header');
  }

  isMobileViewport() {
    return window.innerWidth <= 990;
  }

  isMobileSearchActive() {
    const header = this.getHeaderElement();
    return Boolean(header?.classList.contains('header--search-mode') && this.isMobileViewport());
  }

  getHeaderHeight() {
    const headerHeightVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const parsedVar = parseFloat(headerHeightVar);
    if (!Number.isNaN(parsedVar)) {
      return parsedVar;
    }
    const header = this.getHeaderElement();
    return header?.offsetHeight || 0;
  }

  setupEventListeners() {
    const allSearchForms = [...new Set(Array.from(this.allSearchInputs, (input) => input.form))];
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    this.input.addEventListener('blur', this.onInputBlur.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));

    if (this.isHeaderSearch) {
      // Handle clicks outside to close flyout
      document.addEventListener('click', this.onDocumentClickHandler);

      // Handle escape key to close flyout
      document.addEventListener('keydown', this.onEscapeKeyHandler);
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    if (!this.isHeaderSearch) return;
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
    if (!this.isMobileSearchActive() || this.shouldOpenMobileDropdownByDefault()) {
      this.openFlyout();
    } else {
      this.searchDropdown?.classList.remove('is-open');
      if (!this.isMobileSearchActive()) {
        this.hideOverlay(true);
      }
    }
  }

  onInputBlur(event) {
    if (!this.isHeaderSearch) return;
    // Delay to allow clicks inside the flyout
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
    this.blurTimeout = setTimeout(() => {
      this.blurTimeout = null;
      if (this.flyoutJustOpened) {
        this.pointerDownInsideFlyout = false;
        return;
      }

      if (this.pointerDownInsideFlyout) {
        this.pointerDownInsideFlyout = false;
        return;
      }

      if (this.isMobileSearchActive()) {
        return;
      }

      if (document.activeElement === this.input) {
        return;
      }

      // Check if the new focused element is inside the flyout
      const flyoutContainsFocus = this.searchDropdown?.contains(document.activeElement);
      if (!flyoutContainsFocus) {
        this.closeFlyout();
      }
    }, 200);
  }

  onDocumentClick(event) {
    if (!this.isHeaderSearch) return;
    if (!this.searchDropdown) return;
    if (this.flyoutJustOpened) return;
    if (this.isMobileSearchActive()) return;

    // Check if click is outside both the input and the flyout
    const clickedInput = event.target.closest('.header__search');
    const clickedFlyout = event.target.closest('[data-header-search-dropdown]');

    if (!clickedInput && !clickedFlyout) {
      this.closeFlyout();
    }
  }

  onEscapeKey(event) {
    if (!this.isHeaderSearch) return;
    if (this.isMobileSearchActive()) return;
    if (event.key === 'Escape' && this.isFlyoutOpen()) {
      event.preventDefault();
      this.closeFlyout();
      this.input.blur();
    }
  }

  openFlyout() {
    if (!this.isHeaderSearch) return;
    if (this.searchDropdown) {
      if (this.isMobileSearchActive() && !this.shouldOpenMobileDropdownByDefault()) {
        return;
      }
      this.flyoutJustOpened = true;
      this.searchDropdown.classList.add('is-open');
      this.showOverlay();
      setTimeout(() => {
        this.flyoutJustOpened = false;
      }, 100);
    }
  }

  closeFlyout(options = {}) {
    if (!this.isHeaderSearch) return;
    const { force = false } = options;
    if (this.isMobileSearchActive() && !force) {
      return;
    }
    if (this.searchDropdown) {
      this.searchDropdown.classList.remove('is-open');
      this.flyoutJustOpened = false;
    }
    this.hideOverlay(force);
    if (this.input === document.activeElement && (force || !this.isMobileSearchActive())) {
      this.input.blur();
    }
  }

  isFlyoutOpen() {
    if (!this.isHeaderSearch) return false;
    return this.searchDropdown?.classList.contains('is-open') || false;
  }

  onPointerDown(event) {
    if (!this.isHeaderSearch) return;
    this.pointerDownInsideFlyout = this.searchDropdown?.contains(event.target) || false;
  }

  disconnectedCallback() {
    if (this.isHeaderSearch) {
      document.removeEventListener('pointerdown', this.onPointerDownHandler);
      document.removeEventListener('click', this.onDocumentClickHandler);
      document.removeEventListener('keydown', this.onEscapeKeyHandler);
    }
    if (this.viewportQuery) {
      if (this.viewportQuery.removeEventListener) {
        this.viewportQuery.removeEventListener('change', this.viewportChangeHandler);
      } else if (this.viewportQuery.removeListener) {
        this.viewportQuery.removeListener(this.viewportChangeHandler);
      }
    }
  }

  showOverlay() {
    if (!this.isHeaderSearch) return;
    this.searchOverlay ??= document.querySelector('[data-header-search-overlay]');
    if (!this.searchOverlay) return;
    const header = this.getHeaderElement();
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    this.searchOverlay.style.top = `${headerBottom}px`;
    this.searchOverlay.classList.add('is-visible');
  }

  hideOverlay(force = false) {
    if (!this.isHeaderSearch) return;
    this.searchOverlay ??= document.querySelector('[data-header-search-overlay]');
    if (!this.searchOverlay) return;
    if (this.isMobileSearchActive() && !force) return;
    this.searchOverlay.classList.remove('is-visible');
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  shouldOpenMobileDropdownByDefault() {
    const defaultAvailable = this.searchDropdown?.dataset?.defaultAvailable === 'true';
    const hasQuery = Boolean(this.input.value.trim());
    return defaultAvailable || hasQuery;
  }

  onViewportChange(event) {
    if (!this.isHeaderSearch) return;
    if (event.matches) {
      this.closeFlyout({ force: true });
    }
  }
}

customElements.define('main-search', MainSearch);
